/**
 * Shared look & feel for the Analysis pages.
 *
 * Palette is derived from the Gulftic logo (deep blue #0a62e8 -> azure #1c84f2 -> sky #36a8ff,\n * charcoal wordmark) so the page feels like part of the brand:
 *  - every source gets one cool-toned accent (blue / cyan / indigo family) + one icon
 *  - every metric has ONE semantic colour (teal-green = done, amber = waiting,
 *    blue = in progress, red = late) that is reused on every card / table.
 */

export const palette = {
  primary: '#1c84f2',
  ink: '#1d2b44',
  muted: '#6b7a90',
  line: '#d5e2f2',
  surface: '#eef4fb',
  success: '#13a98b',
  warning: '#f0a73a',
  info: '#0a62e8',
  danger: '#e5484d',
  neutral: '#8593a8',
}

const FALLBACK_SOURCE = { color: '#64748b', icon: 'DatabaseIcon', label: '' }

export const sourceTheme = {
  goeic: { color: '#0fb5c9', icon: 'TruckIcon', label: 'GOEIC' },
  halal: { color: '#13a98b', icon: 'AwardIcon', label: 'Halal' },
  iso: { color: '#6a63f0', icon: 'ShieldIcon', label: 'ISO' },
  jeem1: { color: '#1c84f2', icon: 'FileTextIcon', label: 'Jeem 1' },
  maneh: { color: '#3a54c4', icon: 'LayersIcon', label: 'Maneh' },
  moiat: { color: '#0a62e8', icon: 'BriefcaseIcon', label: 'MOIAT' },
  moph: { color: '#34b3f0', icon: 'ActivityIcon', label: 'MOPH' },
  saber: { color: '#4a6fa5', icon: 'CheckSquareIcon', label: 'Saber' },
  sfda: { color: '#7b8cf2', icon: 'PackageIcon', label: 'SFDA' },
  'sfda-eg': { color: '#a3b1f7', icon: 'PackageIcon', label: 'SFDA EG' },
  sls: { color: '#64748b', icon: 'ToolIcon', label: 'SLS' },
}

export function getSourceTheme(source) {
  const key = String(source || '').toLowerCase()
  const found = sourceTheme[key]
  if (found) return found
  return { ...FALLBACK_SOURCE, label: key ? key.toUpperCase() : 'Unknown' }
}

/** Convert hex colour to an rgba() string - used for soft tinted backgrounds. */
export function tint(hex, alpha = 0.12) {
  const clean = String(hex).replace('#', '')
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean
  const n = parseInt(full, 16)
  // eslint-disable-next-line no-bitwise
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/**
 * Metrics that every source has. Everything else coming from the API is a
 * source specific "status" and is rendered in the breakdown list.
 */
export const coreMetrics = [
  {
    key: 'total_count', label: 'Total', icon: 'BarChart2Icon', color: palette.primary,
  },
  {
    key: 'certificate_issued', label: 'Certificates Issued', icon: 'AwardIcon', color: palette.success,
  },
  {
    key: 'completed', label: 'Completed', icon: 'CheckCircleIcon', color: palette.success,
  },
  {
    key: 'in_turn', label: 'In Turn', icon: 'RepeatIcon', color: palette.info,
  },
  {
    key: 'pending', label: 'Pending', icon: 'ClockIcon', color: palette.warning,
  },
  {
    key: 'total_pending', label: 'Total Pending', icon: 'ClockIcon', color: palette.warning,
  },
  {
    key: 'total_load', label: 'Total Load', icon: 'ActivityIcon', color: palette.info,
  },
  {
    key: 'second_review', label: 'Second Review', icon: 'RefreshCwIcon', color: palette.info,
  },
  {
    key: 'Send_back_to_Client', label: 'Sent Back to Client', icon: 'CornerUpLeftIcon', color: palette.neutral,
  },
  {
    key: 'late_requests', label: 'Late', icon: 'AlertTriangleIcon', color: palette.danger,
  },
  {
    key: 'Not_Found', label: 'Not Found', icon: 'SlashIcon', color: palette.neutral,
  },
  {
    key: 'canceled', label: 'Canceled', icon: 'XIcon', color: palette.neutral,
  },
]

const coreKeys = coreMetrics.map(m => m.key)

/** API sends numbers as numbers *and* as strings ("2"), normalise both. */
export function toNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

export function formatNumber(value) {
  return toNumber(value).toLocaleString('en-US')
}

/** "Wait_for_the_payment_of_license_invoice" -> "Wait for the payment of license invoice" */
export function humanize(key) {
  const text = String(key || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
  return text || 'Unspecified'
}

/**
 * Splits one analysis object (a source of the requests analysis or one source of
 * an engineer) into the core numbers and the source specific status breakdown.
 */
export function splitAnalysis(analysis = {}) {
  const core = coreMetrics
    .filter(metric => Object.prototype.hasOwnProperty.call(analysis, metric.key))
    .map(metric => ({ ...metric, value: toNumber(analysis[metric.key]) }))

  const breakdown = Object.keys(analysis)
    .filter(key => key !== 'source' && !coreKeys.includes(key))
    .map(key => ({ key, label: humanize(key), value: toNumber(analysis[key]) }))
    .sort((a, b) => b.value - a.value)

  return { core, breakdown }
}
